import { useState, useEffect, useRef, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { createChart, CandlestickSeries, HistogramSeries, LineSeries, BaselineSeries, createSeriesMarkers } from "lightweight-charts";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Crosshair, Maximize2, Minimize2, BarChart3, TrendingUp,
  ArrowUpRight, ArrowDownRight, Activity, SlidersHorizontal, ChevronDown, Gauge,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { chartTheme, CANDLE_COLORS } from "@/components/chartTheme";

const TIMEFRAMES = [
  { value: "1m", label: "1m" },
  { value: "3m", label: "3m" },
  { value: "5m", label: "5m" },
  { value: "15m", label: "15m" },
  { value: "30m", label: "30m" },
  { value: "1h", label: "1H" },
  { value: "4h", label: "4H" },
  { value: "1d", label: "1D" },
];

const INSTRUMENTS = [
  { value: "MNQ", label: "MNQ", name: "Micro Nasdaq" },
  { value: "MES", label: "MES", name: "Micro S&P" },
  { value: "MCL", label: "MCL", name: "Micro Crude" },
  { value: "MGC", label: "MGC", name: "Micro Gold" },
  { value: "M2K", label: "M2K", name: "Micro Russell" },
];

// RSI pane styled like TradingView: purple RSI, yellow RSI-based MA, tinted 30-70 band
const RSI_COLOR = "#7e57c2";
const rsiMaColor = (dark) => (dark ? "#fdd835" : "#ca8a04");
const rsiBandColor = (dark) => (dark ? "rgba(126, 87, 194, 0.10)" : "rgba(126, 87, 194, 0.07)");

const VOLUME_COLORS = { up: "rgba(34, 197, 94, 0.2)", down: "rgba(239, 68, 68, 0.2)" };

// ── EMA calculation ──────────────────────────────────────────────────
function calcEMA(data, period) {
  const result = [];
  if (data.length < period) return result;
  const k = 2 / (period + 1);
  let ema = data.slice(0, period).reduce((s, d) => s + d.close, 0) / period;
  for (let i = period - 1; i < data.length; i++) {
    ema = data[i].close * k + ema * (1 - k);
    result.push({ time: data[i].time, value: parseFloat(ema.toFixed(2)) });
  }
  return result;
}

// ── VWAP calculation ─────────────────────────────────────────────────
function calcVWAP(data) {
  const result = [];
  let cumTPV = 0;
  let cumVol = 0;
  let currentDay = null;
  for (const d of data) {
    const day = new Date(d.time * 1000).toISOString().slice(0, 10);
    if (day !== currentDay) {
      cumTPV = 0;
      cumVol = 0;
      currentDay = day;
    }
    const tp = (d.high + d.low + d.close) / 3;
    const vol = d.volume || 1;
    cumTPV += tp * vol;
    cumVol += vol;
    result.push({ time: d.time, value: parseFloat((cumTPV / cumVol).toFixed(2)) });
  }
  return result;
}

// ── RSI calculation (Wilder's smoothing) ─────────────────────────────
function calcRSI(data, period = 14) {
  const result = [];
  if (data.length <= period) return result;
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const ch = data[i].close - data[i - 1].close;
    if (ch > 0) avgGain += ch; else avgLoss -= ch;
  }
  avgGain /= period;
  avgLoss /= period;
  const rsi = () => (avgLoss === 0 ? (avgGain === 0 ? 50 : 100) : 100 - 100 / (1 + avgGain / avgLoss));
  result.push({ time: data[period].time, value: parseFloat(rsi().toFixed(2)) });
  for (let i = period + 1; i < data.length; i++) {
    const ch = data[i].close - data[i - 1].close;
    avgGain = (avgGain * (period - 1) + (ch > 0 ? ch : 0)) / period;
    avgLoss = (avgLoss * (period - 1) + (ch < 0 ? -ch : 0)) / period;
    result.push({ time: data[i].time, value: parseFloat(rsi().toFixed(2)) });
  }
  return result;
}

// Simple moving average over {time, value} points (TradingView's "RSI-based MA")
function calcSMA(points, period) {
  const result = [];
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    sum += points[i].value;
    if (i >= period) sum -= points[i - period].value;
    if (i >= period - 1) result.push({ time: points[i].time, value: parseFloat((sum / period).toFixed(2)) });
  }
  return result;
}

function RsiLegend({ top, rsiVal, maVal, dark }) {
  if (top == null) return null;
  return (
    <div data-testid="chart-rsi-legend" style={{ top: top + 4 }}
      className="absolute left-1.5 sm:left-2 z-10 flex flex-wrap items-center gap-x-1.5 gap-y-0 rounded px-1.5 py-0.5 bg-[hsl(var(--ops-bg))]/70 pointer-events-none">
      <span className="text-[11px] font-semibold text-[hsl(var(--ops-text))]">RSI</span>
      <span className="text-[10px] text-[hsl(var(--ops-text-muted))]">14 close</span>
      {rsiVal != null && <span className="text-[11px] font-mono-num" style={{ color: RSI_COLOR }}>{rsiVal.toFixed(2)}</span>}
      {maVal != null && <span className="text-[11px] font-mono-num" style={{ color: rsiMaColor(dark) }}>{maVal.toFixed(2)}</span>}
    </div>
  );
}

function LegendOverlay({ data, instrument, ema9Val, ema21Val, vwapVal }) {
  if (!data) return null;
  const isUp = data.close >= data.open;
  const clr = isUp ? "ops-long" : "ops-short";
  return (
    <div data-testid="chart-legend-overlay"
      className="chart-legend absolute top-1.5 left-1.5 right-[64px] sm:right-auto sm:top-2 sm:left-2 z-10 flex flex-wrap items-center gap-x-1.5 sm:gap-x-3 gap-y-0 bg-[hsl(var(--ops-bg))]/85 backdrop-blur-sm rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 border border-[hsl(var(--ops-border))] sm:max-w-[600px] pointer-events-none">
      <span className="text-[11px] font-semibold text-[hsl(var(--ops-brand))]">{instrument}</span>
      <span className="text-[10px] text-[hsl(var(--ops-text-muted))]">O</span>
      <span className={`text-[11px] font-mono-num ${clr}`}>{data.open?.toFixed(2)}</span>
      <span className="text-[10px] text-[hsl(var(--ops-text-muted))]">H</span>
      <span className={`text-[11px] font-mono-num ${clr}`}>{data.high?.toFixed(2)}</span>
      <span className="text-[10px] text-[hsl(var(--ops-text-muted))]">L</span>
      <span className={`text-[11px] font-mono-num ${clr}`}>{data.low?.toFixed(2)}</span>
      <span className="text-[10px] text-[hsl(var(--ops-text-muted))]">C</span>
      <span className={`text-[11px] font-mono-num font-semibold ${clr}`}>{data.close?.toFixed(2)}</span>
      {data.volume != null && (
        <span className="hidden sm:contents"><span className="text-[10px] text-[hsl(var(--ops-text-muted))]">Vol</span>
        <span className="text-[11px] font-mono-num text-[hsl(var(--ops-text))]">{data.volume?.toLocaleString()}</span></span>
      )}
      {ema9Val != null && (
        <><span className="text-[10px]" style={{ color: "#f59e0b" }}>EMA9</span>
        <span className="text-[11px] font-mono-num" style={{ color: "#f59e0b" }}>{ema9Val}</span></>
      )}
      {ema21Val != null && (
        <><span className="text-[10px]" style={{ color: "#8b5cf6" }}>EMA21</span>
        <span className="text-[11px] font-mono-num" style={{ color: "#8b5cf6" }}>{ema21Val}</span></>
      )}
      {vwapVal != null && (
        <><span className="text-[10px]" style={{ color: "#06b6d4" }}>VWAP</span>
        <span className="text-[11px] font-mono-num" style={{ color: "#06b6d4" }}>{vwapVal}</span></>
      )}
    </div>
  );
}

export default function Charts() {
  const { autoRefresh } = useOutletContext();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const seriesRefs = useRef({});

  const [instrument, setInstrument] = useState("MNQ");
  const [timeframe, setTimeframe] = useState("5m");
  const [candles, setCandles] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [legendData, setLegendData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showMarkers, setShowMarkers] = useState(true);
  const [showEMA, setShowEMA] = useState(true);
  const [showVWAP, setShowVWAP] = useState(false);
  const [showRSI, setShowRSI] = useState(false);
  const [magnet, setMagnet] = useState(false);     // crosshair snaps to candle closes
  const magnetRef = useRef(false);
  magnetRef.current = magnet;
  const [isPhone, setIsPhone] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const baseHeight = isPhone ? 420 : 560;
  const [lastCandle, setLastCandle] = useState(null);
  const [ema9Val, setEma9Val] = useState(null);
  const [ema21Val, setEma21Val] = useState(null);
  const [vwapVal, setVwapVal] = useState(null);
  const [rsiVal, setRsiVal] = useState(null);
  const [rsiMaVal, setRsiMaVal] = useState(null);
  const [rsiTop, setRsiTop] = useState(null);    // px offset of the RSI pane inside the chart, for its legend
  const [live, setLive] = useState(null);          // last live update {price, at, source, mode}
  const [nowTick, setNowTick] = useState(Date.now());
  const [chartVersion, setChartVersion] = useState(0);
  const candlesRef = useRef([]);                   // what the chart is showing right now (REST + live)
  const liveBarsRef = useRef({ key: "", bars: [] }); // newest live bars for this instrument/timeframe
  const hoverRef = useRef(false);
  const hasData = candles.length > 0;
  const candlesStateRef = useRef([]);
  candlesStateRef.current = candles;

  const fetchData = useCallback(async () => {
    try {
      const [candleRes, markerRes] = await Promise.all([
        api.get(`/ops/chart/candles?instrument=${instrument}&timeframe=${timeframe}&limit=2000`),
        api.get(`/ops/chart/markers?instrument=${instrument}`),
      ]);
      setCandles(candleRes.data.candles || []);
      setMarkers(markerRes.data.markers || []);
      const c = candleRes.data.candles;
      if (c?.length > 0 && !liveBarsRef.current.bars.length) {
        setLastCandle(c[c.length - 1]);
        if (!hoverRef.current) setLegendData(c[c.length - 1]);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [instrument, timeframe]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);
  useEffect(() => {
    if (!autoRefresh) return;
    const iv = setInterval(fetchData, 15000);  // history refresh; the live candle streams separately
    return () => clearInterval(iv);
  }, [autoRefresh, fetchData]);

  // Merge the newest live bars into a candle array (live data can be newer than the REST history).
  const mergeLive = useCallback((arr) => {
    const out = arr.slice();
    const lb = liveBarsRef.current;
    if (lb.key !== `${instrument}|${timeframe}`) return out;
    for (const b of lb.bars) {
      if (!out.length) { out.push(b); continue; }
      const last = out[out.length - 1];
      if (b.time === last.time) out[out.length - 1] = b;
      else if (b.time > last.time) out.push(b);
    }
    return out;
  }, [instrument, timeframe]);

  // Build chart (only when the chart's shape changes, not on every data refresh, so zoom/scroll stay put)
  useEffect(() => {
    if (loading || !chartRef.current || candlesStateRef.current.length === 0) return;
    const candles = mergeLive(candlesStateRef.current);
    candlesRef.current = candles;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.remove();
      chartInstanceRef.current = null;
      seriesRefs.current = {};
    }

    const container = chartRef.current;
    const chartHeight = expanded ? window.innerHeight - (isPhone ? 120 : 140) : baseHeight;
    const theme0 = chartTheme(isDark);
    if (isPhone) {
      theme0.layout = { ...theme0.layout, fontSize: 10 };
      theme0.rightPriceScale = { ...theme0.rightPriceScale, minimumWidth: 0 };
    }
    if (magnetRef.current) theme0.crosshair = { ...theme0.crosshair, mode: 1 };
    const chart = createChart(container, {
      ...theme0,
      width: container.clientWidth,
      height: chartHeight,
    });

    // Candlestick series
    const candleSeries = chart.addSeries(CandlestickSeries, {
      ...CANDLE_COLORS,
      borderVisible: true,
      wickVisible: true,
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
    });
    candleSeries.setData(candles);
    seriesRefs.current.candle = candleSeries;

    // Volume
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
    });
    chart.priceScale("volume").applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    volumeSeries.setData(candles.map((c) => ({
      time: c.time,
      value: c.volume || 0,
      color: c.close >= c.open ? VOLUME_COLORS.up : VOLUME_COLORS.down,
    })));
    seriesRefs.current.volume = volumeSeries;

    // EMA 9
    let ema9Series = null;
    if (showEMA) {
      const ema9Data = calcEMA(candles, 9);
      if (ema9Data.length > 0) {
        ema9Series = chart.addSeries(LineSeries, {
          color: "#f59e0b",
          lineWidth: 1,
          lineStyle: 0,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        ema9Series.setData(ema9Data);
        seriesRefs.current.ema9 = ema9Series;
        setEma9Val(ema9Data[ema9Data.length - 1]?.value);
      }
    }

    // EMA 21
    let ema21Series = null;
    if (showEMA) {
      const ema21Data = calcEMA(candles, 21);
      if (ema21Data.length > 0) {
        ema21Series = chart.addSeries(LineSeries, {
          color: "#8b5cf6",
          lineWidth: 1,
          lineStyle: 0,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        ema21Series.setData(ema21Data);
        seriesRefs.current.ema21 = ema21Series;
        setEma21Val(ema21Data[ema21Data.length - 1]?.value);
      }
    }

    // VWAP
    let vwapSeries = null;
    if (showVWAP) {
      const vwapData = calcVWAP(candles);
      if (vwapData.length > 0) {
        vwapSeries = chart.addSeries(LineSeries, {
          color: "#06b6d4",
          lineWidth: 1.5,
          lineStyle: 0,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        vwapSeries.setData(vwapData);
        seriesRefs.current.vwap = vwapSeries;
        setVwapVal(vwapData[vwapData.length - 1]?.value);
      }
    }

    // RSI 14 in its own pane below the price (shares the time scale + crosshair with the main pane)
    let rsiSeries = null;
    let rsiMaSeries = null;
    let syncRsiTop = () => {};
    if (showRSI) {
      const rsiData = calcRSI(candles, 14);
      const rsiMaData = calcSMA(rsiData, 14);
      const fixedRange = () => ({ priceRange: { minValue: 0, maxValue: 100 } });
      // 30-70 band: a baseline series pinned at 70 with its baseline at 30, line hidden, only the fill shows
      const band = chart.addSeries(BaselineSeries, {
        baseValue: { type: "price", price: 30 },
        lineVisible: false,
        topLineColor: "transparent", bottomLineColor: "transparent",
        topFillColor1: rsiBandColor(isDark), topFillColor2: rsiBandColor(isDark),
        bottomFillColor1: "transparent", bottomFillColor2: "transparent",
        priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        autoscaleInfoProvider: fixedRange,
      }, 1);
      band.setData(candles.map((c) => ({ time: c.time, value: 70 })));
      rsiSeries = chart.addSeries(LineSeries, {
        color: RSI_COLOR,
        lineWidth: 1.5,
        priceLineVisible: false,
        lastValueVisible: true,
        crosshairMarkerVisible: false,
        priceFormat: { type: "price", precision: 2, minMove: 0.01 },
        autoscaleInfoProvider: fixedRange,
      }, 1);
      rsiSeries.setData(rsiData);
      rsiMaSeries = chart.addSeries(LineSeries, {
        color: rsiMaColor(isDark),
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: true,
        crosshairMarkerVisible: false,
        priceFormat: { type: "price", precision: 2, minMove: 0.01 },
        autoscaleInfoProvider: fixedRange,
      }, 1);
      rsiMaSeries.setData(rsiMaData);
      const guide = { color: theme0.layout.textColor, lineWidth: 1, lineStyle: 2, axisLabelVisible: false, title: "" };
      rsiSeries.createPriceLine({ ...guide, price: 70 });
      rsiSeries.createPriceLine({ ...guide, price: 50, color: theme0.timeScale.borderColor });
      rsiSeries.createPriceLine({ ...guide, price: 30 });
      chart.priceScale("right", 1).applyOptions({ scaleMargins: { top: 0.06, bottom: 0.06 } });
      const panes = chart.panes();
      panes[0]?.setStretchFactor(72);
      panes[1]?.setStretchFactor(28);
      seriesRefs.current.rsi = rsiSeries;
      seriesRefs.current.rsiMa = rsiMaSeries;
      seriesRefs.current.rsiBand = band;
      setRsiVal(rsiData[rsiData.length - 1]?.value ?? null);
      setRsiMaVal(rsiMaData[rsiMaData.length - 1]?.value ?? null);
      // where the RSI pane starts (re-measured on resize and after dragging the pane separator)
      syncRsiTop = () => {
        try {
          const el = chart.panes()[1]?.getHTMLElement();
          if (el) setRsiTop(el.getBoundingClientRect().top - container.getBoundingClientRect().top);
        } catch (e) { /* chart already removed */ }
      };
      requestAnimationFrame(syncRsiTop);
      container.addEventListener("pointerup", syncRsiTop);
    } else {
      setRsiTop(null);
    }

    // Crosshair move for legend values
    chart.subscribeCrosshairMove((param) => {
      if (!param.time || !param.seriesData) {
        hoverRef.current = false;
        const arr = candlesRef.current;
        if (arr.length) setLegendData(arr[arr.length - 1]);
        return;
      }
      hoverRef.current = true;
      const cd = param.seriesData.get(candleSeries);
      const vd = param.seriesData.get(volumeSeries);
      if (cd) {
        setLegendData({ open: cd.open, high: cd.high, low: cd.low, close: cd.close, volume: vd?.value });
      }
      if (ema9Series) {
        const v = param.seriesData.get(ema9Series);
        setEma9Val(v?.value ?? null);
      }
      if (ema21Series) {
        const v = param.seriesData.get(ema21Series);
        setEma21Val(v?.value ?? null);
      }
      if (vwapSeries) {
        const v = param.seriesData.get(vwapSeries);
        setVwapVal(v?.value ?? null);
      }
      if (rsiSeries) {
        const v = param.seriesData.get(rsiSeries);
        setRsiVal(v?.value ?? null);
        setRsiMaVal(param.seriesData.get(rsiMaSeries)?.value ?? null);
      }
    });

    chart.timeScale().fitContent();
    chartInstanceRef.current = chart;
    setChartVersion((v) => v + 1);

    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        if (e.contentRect.width > 0 && chart) chart.applyOptions({ width: e.contentRect.width });
      }
      requestAnimationFrame(syncRsiTop);
    });
    ro.observe(container);

    return () => { ro.disconnect(); container.removeEventListener("pointerup", syncRsiTop); chart.remove(); chartInstanceRef.current = null; seriesRefs.current = {}; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasData, expanded, showMarkers, showEMA, showVWAP, showRSI, instrument, timeframe, isDark, isPhone]);

  useEffect(() => {
    if (chartInstanceRef.current) chartInstanceRef.current.applyOptions({ crosshair: { mode: magnet ? 1 : 0 } });
  }, [magnet]);

  // Instrument / timeframe change: start from the fresh REST history
  useEffect(() => { candlesRef.current = []; liveBarsRef.current = { key: "", bars: [] }; setLive(null); }, [instrument, timeframe]);

  // History refresh: update the series in place (no rebuild)
  useEffect(() => {
    const s = seriesRefs.current;
    if (!s.candle || candles.length === 0) return;
    const data = mergeLive(candles);
    candlesRef.current = data;
    try {
      s.candle.setData(data);
      s.volume?.setData(data.map((c) => ({ time: c.time, value: c.volume || 0, color: c.close >= c.open ? VOLUME_COLORS.up : VOLUME_COLORS.down })));
      if (s.ema9) s.ema9.setData(calcEMA(data, 9));
      if (s.ema21) s.ema21.setData(calcEMA(data, 21));
      if (s.vwap) s.vwap.setData(calcVWAP(data));
      if (s.rsi) {
        const r = calcRSI(data, 14);
        s.rsi.setData(r);
        s.rsiMa?.setData(calcSMA(r, 14));
        s.rsiBand?.setData(data.map((c) => ({ time: c.time, value: 70 })));
      }
    } catch (e) { /* keep the chart alive */ }
  }, [candles, mergeLive]);

  // Trade markers (separate, so a markers refresh does not rebuild the chart)
  useEffect(() => {
    const s = seriesRefs.current;
    const candles = candlesRef.current;
    if (!s.candle || candles.length === 0) return;
    let deduped = [];
    if (showMarkers && markers.length > 0) {
      const minTime = candles[0].time;
      const maxTime = candles[candles.length - 1].time;

      // Build a map for O(1) candle time lookup
      const candleTimeSet = new Set(candles.map(c => c.time));
      const candleTimes = candles.map(c => c.time);

      const snapToCandle = (t) => {
        if (candleTimeSet.has(t)) return t;
        // Binary search for nearest
        let lo = 0, hi = candleTimes.length - 1;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (candleTimes[mid] < t) lo = mid + 1;
          else hi = mid;
        }
        if (lo > 0 && Math.abs(candleTimes[lo - 1] - t) < Math.abs(candleTimes[lo] - t)) {
          return candleTimes[lo - 1];
        }
        return candleTimes[lo];
      };

      const validMarkers = markers
        .filter((m) => m.time >= minTime && m.time <= maxTime)
        .map((m) => ({ ...m, time: snapToCandle(m.time) }));

      // Deduplicate: keep only one marker per time+position combo
      const seen = new Map();
      deduped = [];
      for (const m of validMarkers) {
        const key = `${m.time}_${m.position}`;
        if (!seen.has(key)) {
          seen.set(key, true);
          deduped.push(m);
        }
      }

      deduped.sort((a, b) => a.time - b.time);
    }
    try {
      if (s.markers) s.markers.setMarkers(deduped);
      else if (deduped.length > 0) s.markers = createSeriesMarkers(s.candle, deduped);
    } catch (e) { /* silent marker errors */ }
  }, [markers, showMarkers, chartVersion]);


  // ── LIVE: developing candle + last price pushed from the server (SSE), 1 s polling fallback ──
  const applyLive = useCallback((msg, mode) => {
    if (!msg || (msg.instrument || "").toUpperCase() !== instrument) return;
    const bars = (msg.bars || []).filter((b) => b && b.time).sort((a, b) => a.time - b.time);
    if (!bars.length) return;
    liveBarsRef.current = { key: `${instrument}|${timeframe}`, bars };
    const s = seriesRefs.current;
    const arr = candlesRef.current;
    let newest = null;
    for (const b of bars) {
      const bar = { time: b.time, open: b.open, high: b.high, low: b.low, close: b.close, volume: b.volume || 0 };
      if (arr.length) {
        const last = arr[arr.length - 1];
        if (bar.time < last.time) continue;
        if (bar.time === last.time) arr[arr.length - 1] = bar; else arr.push(bar);
      }
      newest = bar;
      if (s.candle) {
        try {
          s.candle.update(bar);
          s.volume?.update({ time: bar.time, value: bar.volume || 0, color: bar.close >= bar.open ? VOLUME_COLORS.up : VOLUME_COLORS.down });
        } catch (e) { /* out-of-order update: the next history refresh fixes it */ }
      }
    }
    if (!newest) return;
    if (s.candle && arr.length) {
      const tail = arr.slice(-600);
      try {
        if (s.ema9) { const e = calcEMA(tail, 9); if (e.length) { s.ema9.update(e[e.length - 1]); if (!hoverRef.current) setEma9Val(e[e.length - 1].value); } }
        if (s.ema21) { const e = calcEMA(tail, 21); if (e.length) { s.ema21.update(e[e.length - 1]); if (!hoverRef.current) setEma21Val(e[e.length - 1].value); } }
        if (s.vwap) { const v = calcVWAP(arr.slice(-1500)); if (v.length) { s.vwap.update(v[v.length - 1]); if (!hoverRef.current) setVwapVal(v[v.length - 1].value); } }
        // full history (O(n), cheap) so the live value matches the history refresh exactly
        if (s.rsi) {
          s.rsiBand?.update({ time: newest.time, value: 70 });
          const r = calcRSI(arr, 14);
          if (r.length) { s.rsi.update(r[r.length - 1]); if (!hoverRef.current) setRsiVal(r[r.length - 1].value); }
          const m = calcSMA(r.slice(-14), 14);
          if (m.length) { s.rsiMa?.update(m[0]); if (!hoverRef.current) setRsiMaVal(m[0].value); }
        }
      } catch (e) { /* ignore */ }
    }
    setLastCandle(newest);
    if (!hoverRef.current) setLegendData(newest);
    const L = msg.last || {};
    const info = { price: L.price ?? newest.close, at: Date.now(), source: L.source, mode,
                   read_at: L.read_at, recv_at: L.recv_at, server_time: msg.server_time };
    setLive(info);
    try {
      const log = (window.__mentoraiLive = window.__mentoraiLive || []);
      log.push(info);
      if (log.length > 2000) log.splice(0, log.length - 2000);
    } catch (e) { /* ignore */ }
  }, [instrument, timeframe]);

  useEffect(() => {
    let es = null, poll = null, closed = false, gotMsg = false, errors = 0;
    const startPolling = () => {
      if (poll || closed) return;
      const tick = async () => {
        try {
          const r = await api.get(`/ops/live/last?instrument=${instrument}&timeframe=${timeframe}`);
          applyLive(r.data, "poll");
        } catch (e) { /* ignore */ }
      };
      tick();
      poll = setInterval(tick, 1000);
    };
    if (typeof window.EventSource === "function") {
      es = new EventSource(`${API}/ops/live/stream?instrument=${instrument}&timeframe=${timeframe}`, { withCredentials: true });
      es.onmessage = (e) => { gotMsg = true; errors = 0; try { applyLive(JSON.parse(e.data), "stream"); } catch (x) { /* ignore */ } };
      es.onerror = () => {
        errors += 1;
        // not authorised by cookie, or a proxy that will not stream: fall back to 1 s polling
        if (!gotMsg || errors >= 5 || es.readyState === 2) { try { es.close(); } catch (x) { /* ignore */ } startPolling(); }
      };
    } else {
      startPolling();
    }
    return () => { closed = true; if (es) es.close(); if (poll) clearInterval(poll); };
  }, [instrument, timeframe, applyLive]);

  // re-render the "updated Xs ago" badge
  useEffect(() => { const iv = setInterval(() => setNowTick(Date.now()), 500); return () => clearInterval(iv); }, []);
  const liveAge = live ? Math.max(0, (nowTick - live.at) / 1000) : null;

  const pnlChange = lastCandle ? lastCandle.close - lastCandle.open : 0;
  const pnlPct = lastCandle && lastCandle.open ? ((pnlChange / lastCandle.open) * 100) : 0;
  const instInfo = INSTRUMENTS.find((i) => i.value === instrument);

  const activeCount = [showMarkers, showEMA, showVWAP, showRSI].filter(Boolean).length;

  return (
    <TooltipProvider delayDuration={200}>
      <div data-testid="ops-charts-page"
        className={`space-y-3 min-w-0 max-w-full ${expanded ? "fixed inset-0 z-50 bg-[hsl(var(--ops-bg))] p-3 sm:p-4 overflow-auto" : ""}`}>

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-x-3 gap-y-2 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-display font-semibold text-[hsl(var(--ops-text))]">{instrument}</h1>
                <span className="text-xs text-[hsl(var(--ops-text-muted))]">{instInfo?.name}</span>
              </div>
              {lastCandle && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                  <span className="text-base font-mono-num font-semibold text-[hsl(var(--ops-text))]">{lastCandle.close?.toFixed(2)}</span>
                  <span className={`text-xs font-mono-num flex items-center gap-0.5 ${pnlChange >= 0 ? "ops-long" : "ops-short"}`}>
                    {pnlChange >= 0 ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
                    {pnlChange >= 0 ? "+" : ""}{pnlChange.toFixed(2)} ({pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(3)}%)
                  </span>
                  <span data-testid="chart-live-badge"
                    title={live ? `last update ${liveAge?.toFixed(1)}s ago via ${live.mode}${live.source ? " (" + live.source + ")" : ""}` : "waiting for live data"}
                    className={`ml-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide ${
                      live && liveAge < 5 ? "bg-[#22c55e]/15 text-[#22c55e]" : live && liveAge < 30 ? "bg-[#f59e0b]/15 text-[#f59e0b]" : "bg-[hsl(var(--ops-surface-2))] text-[hsl(var(--ops-text-muted))]"}`}>
                    <span className={`size-1.5 rounded-full ${live && liveAge < 5 ? "bg-[#22c55e] animate-pulse" : live && liveAge < 30 ? "bg-[#f59e0b]" : "bg-[hsl(var(--ops-text-muted))]"}`} />
                    {live ? (liveAge < 5 ? "LIVE" : `LIVE ${Math.round(liveAge)}s`) : "CONNECTING"}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div data-testid="chart-toolbar" className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <Select value={instrument} onValueChange={setInstrument}>
              <SelectTrigger data-testid="chart-instrument-select" aria-label="Instrument" className="w-[84px] sm:w-[100px] h-8 text-xs bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
                {INSTRUMENTS.map((i) => (<SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>))}
              </SelectContent>
            </Select>

            {/* Timeframe: one compact dropdown instead of a row of 8 buttons */}
            <Select value={timeframe} onValueChange={setTimeframe}>
              <SelectTrigger data-testid="chart-timeframe-select" aria-label="Timeframe" className="w-[76px] h-8 text-xs font-medium bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-brand))]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
                {TIMEFRAMES.map((tf) => (<SelectItem key={tf.value} value={tf.value} data-testid={`chart-tf-${tf.value}`}>{tf.label}</SelectItem>))}
              </SelectContent>
            </Select>

            {/* Indicators & tools in one small menu */}
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="chart-indicators-menu" aria-label="Indicators"
                  className="h-8 px-2 sm:px-2.5 rounded-md flex items-center gap-1.5 text-xs border border-[hsl(var(--ops-border))] bg-[hsl(var(--ops-surface))] text-[hsl(var(--ops-text))] hover:bg-[hsl(var(--ops-surface-2))] transition-colors duration-150">
                  <SlidersHorizontal className="size-3.5" />
                  <span className="hidden sm:inline">Indicators</span>
                  {activeCount > 0 && <span className="text-[10px] font-semibold text-[hsl(var(--ops-brand))]">{activeCount}</span>}
                  <ChevronDown className="size-3 opacity-60 hidden sm:block" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={6} data-testid="chart-indicators-panel"
                className="w-56 p-1.5 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
                {[
                  { id: "markers", label: "Trades on chart", icon: BarChart3, color: "hsl(var(--ops-brand))", on: showMarkers, set: setShowMarkers },
                  { id: "ema", label: "EMA 9 / 21", icon: Activity, color: "#f59e0b", on: showEMA, set: setShowEMA },
                  { id: "vwap", label: "VWAP", icon: TrendingUp, color: "#06b6d4", on: showVWAP, set: setShowVWAP },
                  { id: "rsi", label: "RSI (14)", icon: Gauge, color: RSI_COLOR, on: showRSI, set: setShowRSI },
                  { id: "crosshair", label: "Snap crosshair", icon: Crosshair, color: "hsl(var(--ops-brand))", on: magnet, set: setMagnet },
                ].map((o) => (
                  <label key={o.id} htmlFor={`chart-opt-${o.id}`} data-testid={`chart-toggle-${o.id}`}
                    className="flex items-center gap-2.5 rounded-md px-2 py-2 cursor-pointer hover:bg-[hsl(var(--ops-surface-2))]">
                    <o.icon className="size-3.5 shrink-0" style={{ color: o.on ? o.color : undefined }} />
                    <span className="flex-1 text-[13px]">{o.label}</span>
                    <Switch id={`chart-opt-${o.id}`} checked={o.on} onCheckedChange={(v) => o.set(!!v)} className="scale-75 origin-right" />
                  </label>
                ))}
              </PopoverContent>
            </Popover>

            <Tooltip>
              <TooltipTrigger asChild>
                <button data-testid="chart-toggle-expand" aria-label={expanded ? "Exit fullscreen" : "Fullscreen"} onClick={() => setExpanded(!expanded)}
                  className="size-8 shrink-0 rounded-md flex items-center justify-center border border-[hsl(var(--ops-border))] bg-[hsl(var(--ops-surface))] text-[hsl(var(--ops-text-muted))] hover:bg-[hsl(var(--ops-surface-2))] transition-colors duration-150">
                  {expanded ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
                </button>
              </TooltipTrigger>
              <TooltipContent>{expanded ? "Exit Fullscreen" : "Fullscreen"}</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Chart */}
        <Card data-testid="chart-container" className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] overflow-hidden relative">
          {loading ? (
            <Skeleton className="bg-[hsl(var(--ops-surface-2))]" style={{ height: expanded ? `calc(100vh - ${isPhone ? 120 : 140}px)` : baseHeight }} />
          ) : candles.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-[hsl(var(--ops-text-muted))]" style={{ height: expanded ? `calc(100vh - ${isPhone ? 120 : 140}px)` : baseHeight }}>
              <BarChart3 className="size-8 mb-2 text-[hsl(var(--ops-brand))]" />
              <span className="text-sm">No chart data available for {instrument}</span>
              <span className="text-xs mt-1">Only MNQ has simulated data currently</span>
            </div>
          ) : (
            <>
              <LegendOverlay data={legendData} instrument={instrument} ema9Val={showEMA ? ema9Val : null} ema21Val={showEMA ? ema21Val : null} vwapVal={showVWAP ? vwapVal : null} />
              {showRSI && <RsiLegend top={rsiTop} rsiVal={rsiVal} maVal={rsiMaVal} dark={isDark} />}
              <div ref={chartRef} data-testid="chart-tradingview" style={{ height: expanded ? `calc(100vh - ${isPhone ? 120 : 140}px)` : baseHeight }} />
            </>
          )}
        </Card>

        {/* Stats bar */}
        {!loading && candles.length > 0 && (
          <div className="flex flex-wrap gap-x-4 sm:gap-x-6 gap-y-1 text-[11px] px-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[hsl(var(--ops-text-muted))]">Candles</span>
              <span className="font-mono-num text-[hsl(var(--ops-text))]">{candles.length.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[hsl(var(--ops-text-muted))]">TF</span>
              <Badge className="rounded text-[9px] bg-[hsl(var(--ops-brand))]/15 text-[hsl(var(--ops-brand))] border-0 px-1.5">{timeframe.toUpperCase()}</Badge>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[hsl(var(--ops-text-muted))]">High</span>
              <span className="font-mono-num text-[hsl(var(--ops-text))]">{Math.max(...candles.map((c) => c.high)).toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[hsl(var(--ops-text-muted))]">Low</span>
              <span className="font-mono-num text-[hsl(var(--ops-text))]">{Math.min(...candles.map((c) => c.low)).toFixed(2)}</span>
            </div>
            {showMarkers && markers.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-[hsl(var(--ops-text-muted))]">Trades</span>
                <span className="font-mono-num text-[hsl(var(--ops-text))]">{Math.floor(markers.length / 2)}</span>
              </div>
            )}
            {showEMA && (<span className="text-[hsl(var(--ops-text-muted))]"><span style={{ color: "#f59e0b" }}>EMA9</span> <span style={{ color: "#8b5cf6" }}>EMA21</span></span>)}
            {showVWAP && (<span style={{ color: "#06b6d4" }}>VWAP</span>)}
            {showRSI && (<span style={{ color: RSI_COLOR }}>RSI14</span>)}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
